<script setup>
import axios from 'axios';
import { BAlert, BForm, BFormCheckbox, BFormGroup, BFormInput, BFormRadioGroup, BModal } from 'bootstrap-vue-next';
import { h, reactive, ref } from 'vue';

import LoadingButton from '~/common/components/LoadingButton.vue';
import { useVueData } from '~/common/useVueData';

const { onboardingIssueUrl, organization } = useVueData();
const COMPANY_SETTINGS_URL = 'https://gitlab.com/-/user_settings/profile#user_company';
const CompanySettingsLink = () =>
  h('a', { href: COMPANY_SETTINGS_URL, target: '_blank' }, 'GitLab profile');

const props = defineProps({
  referrer: {
    type: String,
    required: false,
    default: null,
  },
});

const model = defineModel({ type: Boolean });
const form = reactive({ reason: '', languages: [], interests: [], tier: 'none' });
const loading = ref(false);
const errorMessage = ref(null);

const tiers = [
  { value: 'none', text: 'None / prefer not to say' },
  { value: 'free', text: 'Free' },
  { value: 'premium_unverified', text: 'Premium' },
  { value: 'ultimate_unverified', text: 'Ultimate' },
  { value: 'dedicated_unverified', text: 'Dedicated' },
];

const interests = [
  { value: 'code', label: 'Development' },
  { value: 'doc', label: 'Documentation' },
  { value: 'ux', label: 'Design / UX' },
  { value: 'support', label: 'Supporting others' },
  { value: 'translate', label: 'Translation' },
  { value: 'social', label: 'Events and content' },
  { value: 'ci', label: 'CI/CD' },
];
const languages = [
  { value: 'rb', label: 'Ruby / Rails' },
  { value: 'js', label: 'JavaScript / TypeScript' },
  { value: 'go', label: 'Golang' },
  { value: 'py', label: 'Python' },
  { value: 'rust', label: 'Rust' },
];

const handleSubmit = async () => {
  if (loading.value) {
    return;
  }

  loading.value = true;
  errorMessage.value = null;
  if (props.referrer != null) {
    form.reason = props.referrer;
  }

  try {
    const { data } = await axios.post('/api/v1/users/submit_onboarding_response', { data: form });
    if (!data.webUrl) {
      throw new Error('Missing onboarding issue URL');
    }

    window.location.href = data.webUrl;
  } catch {
    errorMessage.value = 'Something went wrong submitting your response. Please try again.';
    loading.value = false;
  }
};
</script>

<template>
  <BModal
    v-model="model"
    title="Start onboarding"
  >
    <template v-if="onboardingIssueUrl">
      <a
        :href="onboardingIssueUrl"
        target="_blank"
      >Check the status of your existing onboarding issue.</a>
    </template>
    <BForm
      v-else
      @submit.prevent
    >
      <BAlert
        :model-value="errorMessage != null"
        variant="danger"
        class="mb-3"
      >
        {{ errorMessage }}
      </BAlert>
      <p>
        To personalize your onboarding experience, please fill out this optional questionnaire.
        <br>
        Your answers will be displayed in your public onboarding issue,
        to which you will be redirected once you select <b>Start</b>.
      </p>
      <BFormGroup
        v-if="referrer == null"
        label="What is your primary reason for joining the community?"
        class="mb-3"
        description="Include a link to an issue if relevant"
      >
        <BFormInput
          v-model="form.reason"
          list="reason"
          max-rows="10"
        />
        <datalist id="reason">
          <option value="Fix a typo, bug, or add a new feature" />
          <option value="Manage an issue/merge request" />
          <option value="Be part of a community" />
          <option value="Part of a co-create project" />
          <option value="Learn about open source" />
          <option value="Join a Hackathon" />
          <option value="Upskill" />
          <option value="Try out GitLab Duo Agent Platform" />
        </datalist>
      </BFormGroup>
      <BFormGroup
        label="How would you like to contribute?"
        class="mb-3"
      >
        <BFormCheckbox
          v-for="interest in interests"
          :key="interest.value"
          v-model="form.interests"
          :value="interest.value"
        >
          {{ interest.label }}
        </BFormCheckbox>
      </BFormGroup>
      <BFormGroup
        v-if="form.interests.includes('code')"
        label="Which languages would you like to use?"
        class="mb-3"
      >
        <BFormCheckbox
          v-for="language in languages"
          :key="language.value"
          v-model="form.languages"
          :value="language.value"
        >
          {{ language.label }}
        </BFormCheckbox>
      </BFormGroup>
      <BFormGroup
        label="Which GitLab offering are you or your organization using?"
        class="mb-3"
      >
        <BFormRadioGroup
          v-model="form.tier"
          class="mb-3"
          :options="tiers"
          stacked
          data-test-id="onboarding-modal-radio-tier"
        />

        <p
          v-if="organization"
          class="mb-1"
        >
          If you do not represent or work for <b>{{ organization }}</b>, please update your
          <CompanySettingsLink />.
        </p>
        <p
          v-else
          class="mb-1"
        >
          We don't have an organization on file for you.
          Please update your
          <CompanySettingsLink />
          if you're happy to share.
        </p>
      </BFormGroup>
    </BForm>
    <template #ok>
      <LoadingButton
        v-if="!onboardingIssueUrl"
        variant="primary"
        :loading="loading"
        loading-text="Starting..."
        @click="handleSubmit"
      >
        Start
      </LoadingButton>
      <span v-else />
    </template>
  </BModal>
</template>
